/**
 * SOLID Review
 * S — Strong. One concern: the entry funnel's plain steps — the guest choice's three ways on, and the auto-host panel's state and its
 *     way back.
 * O — Good. Another step is one more action.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The auto-host's snapshot and the four moves.
 * D — Strong. One use case, the client view, the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import type { ClientRequests } from '../client/ClientRequests';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const TO_GAME: TClientUseCaseInput<'navigate'> = { screen: 'GAME' };
const TO_LOGIN: TClientUseCaseInput<'navigate'> = { screen: 'LOGIN' };
const TO_ENTER_CODE: TClientUseCaseInput<'navigate'> = { screen: 'ENTER_CODE' };
const TO_WELCOME: TClientUseCaseInput<'navigate'> = { screen: 'WELCOME' };

/** The auto-host panel as its view renders it. */
export interface IStateFunnelSteps {
  /** The funnel decided "host a new table" (no code entered): the entry is creating it, or failed to. */
  readonly autoHosting: boolean;
  readonly hostError: TClientViewState['hostError'];
}

/**
 * THE FUNNEL'S PLAIN STEPS (frontend-framework-oo P5, 2026-10-30, §28.298) — `components/auth/GuestChoiceScreen.tsx`'s three ways on
 * (play as a guest, sign in with email, back to the code) and `components/game/MainMenuPanel.tsx`'s auto-host state and its way back to
 * the funnel. Where the funnel goes is the session's (`navigate`, strict-ports FE2).
 */
export class FunnelStepsViewModel implements IServiceViewModel<IStateFunnelSteps> {
  private shown: { readonly autoHosting: boolean; readonly hostError: IStateFunnelSteps['hostError']; readonly steps: IStateFunnelSteps } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'navigate'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
  ) {}

  private go(to: TClientUseCaseInput<'navigate'>): void {
    void this.useCases.navigate(this.requests.context(), to);
  }

  readonly playAsGuest = (): void => this.go(TO_GAME);
  readonly signInWithEmail = (): void => this.go(TO_LOGIN);
  readonly backToCode = (): void => this.go(TO_ENTER_CODE);
  /** The auto-host failed: back to the front screen to choose by hand. */
  readonly chooseManually = (): void => this.go(TO_WELCOME);

  getSnapshot(): IStateFunnelSteps {
    const view = this.view.getSnapshot();
    const autoHosting = !!(view.session.hostOnEntry || view.hosting);
    const { hostError } = view;
    if (this.shown !== null && this.shown.autoHosting === autoHosting && this.shown.hostError === hostError) return this.shown.steps;
    const steps: IStateFunnelSteps = { autoHosting, hostError };
    this.shown = { autoHosting, hostError, steps };
    return steps;
  }

  subscribe(listener: () => void): () => void {
    return this.view.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
